/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { useState, type ReactNode } from 'react';
import { Alert, AlertTitle, Box, Button, Divider, Typography, List, ListItem, ListItemText } from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import type { ErrorFallbackProps } from './types';
import { IssueTrackerLink } from '../IssueTrackerLink/IssueTrackerLink';

export function ErrorFallback( { error, resetErrorBoundary }: ErrorFallbackProps ): ReactNode {
	const [ isErrorDataCopied, setIsErrorDataCopied ] = useState( false );

	const onClickCopyHandler = async () => {
		const errorData = {
			message: error.toString(),
			data: error.appData
		};

		const encodedErrorData = btoa( JSON.stringify( errorData ) );

		await navigator.clipboard.writeText( encodedErrorData );

		setIsErrorDataCopied( true );

		setTimeout( () => setIsErrorDataCopied( false ), 2000 );
	};

	const styles = {
		list: {
			listStyleType: 'disc',
			pl: 2,
			'& .MuiListItem-root': {
				display: 'list-item',
				padding: 0,
				margin: 0,
				fontSize: '13px'
			},
			'& .MuiListItem-root .MuiListItemText-root': {
				marginTop: 0,
				marginBottom: 0
			}
		},
		listItem: {
			'& .MuiTypography-root': {
				fontSize: '14px'
			}
		}
	};

	return (
		<Alert
			severity={ 'error' }
			elevation={ 2 }
			sx={ {
				margin: '16px auto',
				padding: '16px 58px 16px 24px;',
				width: 'max-content',
				maxWidth: '70%'
			} }
		>
			<AlertTitle sx={ { fontWeight: 'bold' } }>Whoops, something went wrong.</AlertTitle>
			<Box sx={ { whiteSpace: 'preserve', wordBreak: 'break-all', margin: '24px 0' } }>
				<code>{ error.toString() }</code>
			</Box>
			<Divider/>
			<Box sx={ { margin: '24px 0' } }>
				<Typography fontSize={ '14px' }>
					We are sorry you are experiencing an unexpected problem.
				</Typography>
				<Typography fontSize={ '14px' }>
					To maintain the high quality of CKEditor 5 Builder, we would appreciate it if you would report the issue to us.
				</Typography>
				<List sx={ styles.list }>
					<ListItem>
						<ListItemText sx={ styles.listItem }>
							Open the <IssueTrackerLink labels={ [ 'type:bug' ] }>CKEditor 5 issue tracker</IssueTrackerLink>.
						</ListItemText>
					</ListItem>
					<ListItem>
						<ListItemText sx={ styles.listItem }>
							Copy the debug data (click the button below).
						</ListItemText>
					</ListItem>
					<ListItem>
						<ListItemText sx={ styles.listItem }>
							Paste them into the new issue.
						</ListItemText>
					</ListItem>
					<ListItem>
						<ListItemText sx={ styles.listItem }>
							Provide more details if you can (steps to reproduce).
						</ListItemText>
					</ListItem>
				</List>
			</Box>
			<Box justifyContent={ 'space-between' } display={ 'flex' }>
				<Button
					id='copy-button'
					variant='contained'
					color='error'
					size='small'
					disabled={ isErrorDataCopied }
					startIcon={ <ContentCopyRoundedIcon /> }
					onClick={ onClickCopyHandler }
				>
					{ isErrorDataCopied && 'Copied, thanks!' || 'Copy debug data' }
				</Button>
				<Button
					id='reload-button'
					variant='outlined'
					color='error'
					size='small'
					startIcon={ <RestartAltIcon /> }
					onClick={ resetErrorBoundary }
				>
					Reload
				</Button>
			</Box>
		</Alert>
	);
}
